'use client';
import React from 'react';
import { MOCK_RESULTS } from './MockExamScreen';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';

interface Props {
  results: typeof MOCK_RESULTS;
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div className="bg-card border border-border rounded-lg shadow-elevated p-3 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className={`font-bold tabular-nums ${val > 90 ? 'text-warning' : 'text-accent'}`}>{val}s avg</p>
      <p className="text-muted-foreground mt-0.5">{val > 90 ? `${val - 90}s over target` : `${90 - val}s under target`}</p>
    </div>
  );
};

export default function ReportTimeAnalysis({ results }: Props) {
  const avgTotal = Math.round(results.timeTaken / 40);

  const chartData = results.byType.map((d) => ({
    id: d.id,
    type: d.type.length > 12 ? d.type.slice(0, 11) + '…' : d.type,
    time: d.time,
  }));

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Time KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { id: 'tk-total', label: 'Total Time Used', value: `${Math.floor(results.timeTaken / 60)}:${String(results.timeTaken % 60).padStart(2, '0')}`, sub: 'of 60:00 available', alert: false },
          { id: 'tk-avg', label: 'Avg Time/Question', value: `${avgTotal}s`, sub: avgTotal > 90 ? `${avgTotal - 90}s over 90s target` : 'Within 90s target', alert: avgTotal > 90 },
          { id: 'tk-slowest', label: 'Slowest Type', value: '121s', sub: 'Matching Headings', alert: true },
          { id: 'tk-fastest', label: 'Fastest Type', value: '78s', sub: 'Short-Answer Questions', alert: false },
        ].map((stat) => (
          <div key={stat.id} className={`card-base p-4 ${stat.alert ? 'border-warning/40 bg-warning-bg/30' : ''}`}>
            <p className="metric-label mb-1">{stat.label}</p>
            <p className={`text-2xl font-bold tabular-nums ${stat.alert ? 'text-warning' : 'text-foreground'}`}>{stat.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Time per question type chart */}
      <div className="card-base p-5">
        <h2 className="section-header mb-1">Time per Question Type</h2>
        <p className="text-xs text-muted-foreground mb-4">Average seconds per question · dashed line = 90s target</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="type"
              tick={{ fontSize: 9, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[60, 140]}
              tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}s`}
            />
            <ReferenceLine
              y={90}
              stroke="var(--danger)"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              label={{ value: '90s target', position: 'insideTopRight', fontSize: 10, fill: 'var(--danger)' }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.5 }} />
            <Bar dataKey="time" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell
                  key={entry.id}
                  fill={entry.time > 90 ? 'var(--warning)' : 'var(--accent)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Passage time table */}
      <div className="card-base p-5">
        <h2 className="section-header mb-4">Passage Time Breakdown</h2>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['Passage', 'Questions', 'Time Allocated', 'Time Used', 'Over/Under', 'Questions/Min'].map((h) => (
                  <th key={`ptt-${h}`} className="text-left text-xs font-medium text-muted-foreground pb-2.5 pr-4 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {results.passageTimeBreakdown.map((row) => {
                const diff = row.actual - row.allocated;
                const qpm = (row.questions / row.actual).toFixed(1);
                return (
                  <tr key={row.id} className="hover:bg-muted/40 transition-colors">
                    <td className="py-3 pr-4 text-sm font-semibold text-foreground">{row.passage}</td>
                    <td className="py-3 pr-4 text-sm tabular-nums text-foreground">{row.questions}</td>
                    <td className="py-3 pr-4 text-sm tabular-nums text-foreground">{row.allocated} min</td>
                    <td className="py-3 pr-4 text-sm tabular-nums text-foreground">{row.actual} min</td>
                    <td className="py-3 pr-4">
                      <span className={`text-sm font-semibold tabular-nums ${diff > 0 ? 'text-warning' : 'text-accent'}`}>
                        {diff > 0 ? `+${diff}` : diff} min
                      </span>
                    </td>
                    <td className="py-3 text-sm tabular-nums text-foreground">{qpm} Q/min</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 p-2.5 rounded-lg bg-warning-bg border border-warning/20">
          <p className="text-xs text-warning font-medium">
            Passages 2 and 3 each overran by 2 minutes — practise your passage-triage order to bank time on Passage 1 for Passage 3
          </p>
        </div>
      </div>
    </div>
  );
}